import { appCtx } from './ApplicationContext'
import { CurrencyCode } from './constants'
import {
	LobbyData,
	TableLobbyDataValidator,
	TournamentLobbyDataValidator,
	SitAndGo,
	Tournament,
	Sort,
	Status,
	RingGame,
	SurgeDataValidator,
	Surge,
} from './data/LobbyData'
import { operatorConfig } from './OperatorConfig'

import { ProtocolUtils } from './ProtocolUtils'
import { RadioGroupFilter } from './filter/LobbyFilters'
import { Settings } from './Settings'
import { _sort } from './script-utils'
import { Injector, Injectable } from './injector'
import { FilterManager } from './FilterManager'
import { LobbyCommunication } from './communication/lobby/LobbyCommunication'
import { fetchJackpotPrizeHistory } from './redux/jackpotSitAndGo/JackpotPrizeHistoryAction'
import { FeatureToggleService } from './services/FeatureToggleService'
import { getQuickSeatStakes } from './QuickSeat'
import { Feature, userFeatureService } from './services/UserFeatureService'
import { combineLatest, merge, Observable, Subject, Subscription } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { SitAndGoApiService } from './services/sit-and-gos/SitAndGoApiService'
import { TournamentUpdatePacket } from './communication/FirebaseProtocol.generated'
import { SitAndGoLobbyUpdate } from './tournaments/models'
import { getStore } from './redux/Store'
import { GameServerStatus } from './redux/game-server/GameServerReducer'
import LobbyListData from './LobbyListData'

export enum GameType {
	Ring,
	SitAndGo,
	Tournament,
	JackpotSitAndGo,
	Surge,
}

@Injectable()
export class LobbyManager {
	currencyFilter: any
	sitAndGoSortingFunction: any
	cashGameSortingFunction: any

	ringGamesLobbyData: LobbyData
	tournamentLobbyData: LobbyData
	sitAndGoLobbyData: LobbyData
	surgeLobbyData: LobbyData

	sitAndGoState: boolean

	lobbyUpdateCallbacks: any[]
	sectionChangeCallbacks: any

	requiredFilters: any
	filtersEnabled: boolean
	state: GameType

	variants: any
	limits: any
	profileView: any

	private _tournamentListFilter$ = new Subject<string>()
	private _tournamentListLoaded$ = new Subject<string>()
	private _sngListFilter$ = new Subject<string>()
	private _sngListLoaded$ = new Subject<string>()
	private _ringGameListFilter$ = new Subject<string>()
	private _ringGameListLoaded$ = new Subject<string>()

	private filterManager = Injector.get(FilterManager)
	private _lobbyCommunication: LobbyCommunication
	private get lobbyCommunication(): LobbyCommunication {
		this._lobbyCommunication = this._lobbyCommunication || Injector.get(LobbyCommunication)
		return this._lobbyCommunication
	}

	private sngLobbySubscription: Subscription

	constructor() {
		this.sitAndGoState = false
		this.lobbyUpdateCallbacks = []
		this.sectionChangeCallbacks = null

		this.requiredFilters = null
		this.filtersEnabled = true
		this.state = null

		this.variants = null
		this.limits = null
		this.profileView = null

		this.sectionChangeCallbacks = []
		this.requiredFilters = []

		this.ringGamesLobbyData = new LobbyData(
			new TableLobbyDataValidator(),
			() => this.requiredFilters.concat(this.filterManager.ringGameFilters),
			(items) => {
				this.state = GameType.Ring
				this.lobbyDataUpdated(GameType.Ring, items)
			},
		)
		this.ringGamesLobbyData.sorters = ringGameSorts

		this.tournamentLobbyData = new LobbyData(
			new TournamentLobbyDataValidator(),
			() => this.requiredFilters.concat(this.filterManager.tournamentFilters),
			(items) => {
				this.state = GameType.Tournament
				this.lobbyDataUpdated(GameType.Tournament, items)
			},
		)
		this.tournamentLobbyData.sorters = tournamentSorts

		this.sitAndGoLobbyData = new LobbyData(
			new TournamentLobbyDataValidator(),
			() => this.requiredFilters.concat(this.filterManager.sitAndGoFilters),
			(items) => {
				this.state = GameType.SitAndGo
				this.lobbyDataUpdated(GameType.SitAndGo, items)
			},
		)
		this.sitAndGoLobbyData.sorters = sngSorts

		LobbyListData.initialize(
			this.sitAndGoLobbyData,
			this.ringGamesLobbyData,
			this.tournamentLobbyData,
		)

		this.surgeLobbyData = new LobbyData(
			new SurgeDataValidator(),
			() => this.requiredFilters,
			(items) => {
				this.state = GameType.Surge
				this.lobbyDataUpdated(GameType.Surge, items)
			},
		)
		this.surgeLobbyData.sorters = ringGameSorts

		this.addCurrencyFilters()

		const currency = userFeatureService.doesUserHaveFeature(Feature.SWITCH_CURRENCY)
			? Settings.getStringValue(Settings.Param.CURRENCY_SELECTED, CurrencyCode.GOLD_COINS)
			: CurrencyCode.GOLD_COINS
		this.currencyFilter.currentFilter = currency
		appCtx.getToolbarManager().currencyChanged(currency)
	}

	sort(sort?: Sort<RingGame | SitAndGo | Tournament | Surge>): void {
		switch (this.state) {
			case GameType.Ring:
				this.ringGamesLobbyData.customSort = sort
				this.ringGamesLobbyData.performSort()
				break
			case GameType.SitAndGo:
				this.sitAndGoLobbyData.customSort = sort
				this.sitAndGoLobbyData.performSort()
				break
			case GameType.Tournament:
				this.tournamentLobbyData.customSort = sort
				this.tournamentLobbyData.performSort()
				break
			case GameType.JackpotSitAndGo:
				break
			case GameType.Surge:
				this.surgeLobbyData.customSort = sort
				this.surgeLobbyData.performSort()
				break
		}
	}

	get onTournamentListFilterChange$(): Observable<string> {
		return this._tournamentListFilter$.asObservable()
	}

	get onTournamentListLoaded$(): Observable<string> {
		return this._tournamentListLoaded$.asObservable()
	}

	tournamentListLoaded() {
		this._tournamentListLoaded$.next()
	}

	tournamentSearchDataChanged(searchFilter: string) {
		this._tournamentListFilter$.next(searchFilter)
	}

	get onSitAndGoListFilterChange$(): Observable<string> {
		return this._sngListFilter$.asObservable()
	}

	get onSitAndGoListLoaded$(): Observable<string> {
		return this._sngListLoaded$.asObservable()
	}

	sitAndGoListLoaded() {
		this._sngListLoaded$.next()
	}

	sitAndGoSearchDataChanged(searchFilter: string) {
		this._sngListFilter$.next(searchFilter)
	}

	get onRingGameListFilterChange$(): Observable<string> {
		return this._ringGameListFilter$.asObservable()
	}

	get onRingGameListLoaded$(): Observable<string> {
		return this._ringGameListLoaded$.asObservable()
	}

	ringGameListLoaded() {
		this._ringGameListLoaded$.next()
	}

	ringGameSearchDataChanged(searchFilter: string) {
		this._ringGameListFilter$.next(searchFilter)
	}

	onBeforeSectionChange(callback) {
		this.sectionChangeCallbacks.push(callback)
	}

	offBeforeSectionChange(callback) {
		const index = this.sectionChangeCallbacks.indexOf(callback)
		if (index > -1) {
			this.sectionChangeCallbacks.splice(index, 1)
		}
	}

	onLobbyData(callback) {
		this.lobbyUpdateCallbacks.push(callback)
	}

	offLobbyData(callback) {
		const index = this.lobbyUpdateCallbacks.indexOf(callback)
		if (index > -1) {
			this.lobbyUpdateCallbacks.splice(index, 1)
		}
	}

	beforeSectionChange(state) {
		this.state = state
		$.each(this.sectionChangeCallbacks, function (i, callback) {
			callback(state)
		})
	}

	lobbyDataUpdated(state: GameType, items) {
		const itemsWithGameType = items.map((i) => Object.assign(i, { gameType: state }))
		this.lobbyUpdateCallbacks.forEach((c) => c(state, itemsWithGameType))
	}

	initPacketHandlers() {
		this.lobbyCommunication.onTableSnapshotList().then((packet) => this.handleTableSnapshotList(packet.snapshots))
		this.lobbyCommunication.onTableUpdateList().then((packet) => this.handleTableUpdateList(packet.updates))
		this.lobbyCommunication.onTableRemoved().then((packet) => this.handleTableRemoved(packet.tableid))
		this.lobbyCommunication
			.onTournamentSnapshotList()
			.then((packet) => this.handleTournamentSnapshotList(packet.snapshots))
		this.lobbyCommunication.onTournamentUpdates().then((packet) => this.handleTournamentUpdates(packet.updates))
		this.lobbyCommunication.onTournamentRemoved().then((packet) => this.handleTournamentRemoved(packet.mttid))
	}

	onLogin(reconnecting) {
		let gameType = Settings.getIntValue(Settings.Param.FILTER_MISC_GAME_TYPE, GameType.JackpotSitAndGo)

		if (gameType === GameType.Surge && !FeatureToggleService.surge.isEnabled) {
			gameType = GameType.Ring
		}

		const currency = userFeatureService.doesUserHaveFeature(Feature.SWITCH_CURRENCY)
			? Settings.getStringValue(Settings.Param.CURRENCY_SELECTED, CurrencyCode.GOLD_COINS)
			: CurrencyCode.GOLD_COINS
		this.setCurrency(currency)

		if (reconnecting) {
			this.lobbyCommunication.subscribe()
		}
		if (!reconnecting) {
			this.updateIFrameUrl('#lobbyTopPromotionsIframe', operatorConfig.getLobbyTopPromotionUrl())
		}

		this.subscribeToGameType(gameType)
		this.initPacketHandlers()
	}

	handleTableSnapshotList(tableSnapshotList) {
		var items = []
		for (var i = 0; i < tableSnapshotList.length; i++) {
			const item = ProtocolUtils.extractTableData(tableSnapshotList[i])
			items.push(item)
		}

		if (this.state === GameType.Surge) {
			this.surgeLobbyData.addItems(items)
		} else {
			this.ringGamesLobbyData.addItems(items) // All tables need to be in the list, quick seat tables wont be visible
			this.ringGamesLobbyData.addItems(
				getQuickSeatStakes((this.ringGamesLobbyData.items.values() as RingGame[]).concat(items)),
			)
		}
	}

	handleTournamentSnapshotList(tournamentSnapshotList) {
		if (tournamentSnapshotList.length > 0 && tournamentSnapshotList[0].address.indexOf('/sitandgo') != -1) {
			this.state = GameType.SitAndGo
		} else {
			this.state = GameType.Tournament
		}

		var items = []
		for (var i = 0; i < tournamentSnapshotList.length; i++) {
			const item = ProtocolUtils.extractTournamentData(tournamentSnapshotList[i])
			items.push(item)
		}
		if (this.state == GameType.SitAndGo) {
			this.sitAndGoLobbyData.addItems(items)
			this.tournamentListLoaded()
		} else {
			this.tournamentLobbyData.addItems(items)
		}
	}

	/**
	 *
	 * @param {FB_PROTOCOL.TournamentUpdatePacket[]} tournamentUpdateList
	 */
	handleTournamentUpdates(tournamentUpdateList: TournamentUpdatePacket[]) {
		const items = tournamentUpdateList.map((update) => ProtocolUtils.extractTournamentData(update))
		if (this.state === GameType.SitAndGo) {
			this.sitAndGoLobbyData.updateItems(items)
		} else {
			this.tournamentLobbyData.updateItems(items)
		}
	}

	handleTableUpdateList(tableUpdateList) {
		var items = []
		for (var i = 0; i < tableUpdateList.length; i++) {
			const item = ProtocolUtils.extractTableData(tableUpdateList[i])
			items.push(item)
		}

		if (this.state === GameType.Surge) {
			this.surgeLobbyData.updateItems(items)
		} else {
			this.ringGamesLobbyData.updateItems(items)
		}
	}

	handleTableRemoved(tableId) {
		if (this.state === GameType.Surge) {
			this.surgeLobbyData.remove(tableId)
		} else {
			this.ringGamesLobbyData.remove(tableId)
		}
	}

	handleTournamentRemoved(tournamentId) {
		this.tournamentLobbyData.remove(tournamentId)
	}

	clearLobby() {
		this.ringGamesLobbyData.clear()
		this.tournamentLobbyData.clear()
		this.sitAndGoLobbyData.clear()
		$('#tableListContainer').empty()
	}

	setCashGameSortingFunction(func) {
		this.cashGameSortingFunction = func
	}

	setSitAndGoSortingFunction(func) {
		this.sitAndGoSortingFunction = func
	}

	updateIFrameUrl(iframe, url) {
		var iframe: any = $(iframe)
		if (url != null && $.trim(url).length > 0) {
			iframe.attr('src', url)
			iframe.show()
		}
	}

	addCurrencyFilters() {
		if (this.currencyFilter != null) {
			var index = this.requiredFilters.indexOf(this.currencyFilter)
			if (index != -1) {
				this.requiredFilters.splice(index, 1)
			}
		}

		var currencies = operatorConfig.getEnabledCurrencies()
		if (currencies.length > 0) {
			this.currencyFilter = new RadioGroupFilter(
				currencies,
				['currencyCode', 'buyInCurrencyCode'],
				'filterButton',
				'code',
				false,
			)
			this.currencyFilter.showIfInGroup = operatorConfig.isListAllEnabledCurrencies()
			this.currencyFilter.changeListener = function (currency) {
				appCtx.getToolbarManager().currencyChanged(currency)
			}
			this.requiredFilters.push(this.currencyFilter)
		} else {
			$('.filter-group.currencies').hide()
		}
	}

	getCurrencyCode(): CurrencyCode {
		return this.currencyFilter.currentFilter
	}

	filterUpdated() {
		var items = null
		if (this.state == GameType.SitAndGo) {
			items = this.sitAndGoLobbyData.getFilteredItems()
		} else if (this.state == GameType.Tournament) {
			items = this.tournamentLobbyData.getFilteredItems()
		} else if (this.state == GameType.Surge) {
			items = this.surgeLobbyData.getFilteredItems()
		} else {
			items = this.ringGamesLobbyData.getFilteredItems()
		}
		this.lobbyDataUpdated(this.state, items)
	}

	setCurrency(id) {
		this.currencyFilter.currentFilter = id
		appCtx.getToolbarManager().currencyChanged(id)
		Settings.setProperty(Settings.Param.CURRENCY_SELECTED, id)
		fetchJackpotPrizeHistory()
		this.filterUpdated()
	}

	subscribeToGameType(gameType: GameType) {
		switch (gameType) {
			case GameType.Ring:
				this.lobbyCommunication.subscribeToCashGames()
				break
			case GameType.Tournament:
				this.lobbyCommunication.subscribeToTournaments()
				break
			case GameType.SitAndGo:
				this.lobbyCommunication.subscribeToSitAndGos()
				break
			case GameType.JackpotSitAndGo:
				this.lobbyCommunication.subscribeToJackpotSitAndGo()
				break
			case GameType.Surge:
				this.lobbyCommunication.subscribeToSurge()
				break
			default:
				const assetNever: never = gameType
				return
		}
	}

	subscribeToPokerLobby() {
		this.lobbyCommunication.subscribeToNewPokerLobby()
	}
}

export const ringGameSorts = [
	(games: RingGame[]) => _sort(games, 'name', true),
	(games: RingGame[]) => _sort(games, 'seated', false),
	(games: RingGame[]) => _sort(games, 'capacity', false),
	(games: RingGame[]) => _sort(games, bigBlindSort),
]
export const sngSorts = [
	(games: SitAndGo[]) => _sort(games, 'name', true),
	(games: SitAndGo[]) => _sort(games, 'registered', false),
	(games: SitAndGo[]) => _sort(games, 'capacity', true),
	(games: SitAndGo[]) => _sort(games, stakesSort),
	(games: SitAndGo[]) => _sort(games, hasPlayersSort),
	(games: SitAndGo[]) => _sort(games, statusSort),
]
export const tournamentSorts = [
	(games: Tournament[]) => _sort(games, 'capacity', false),
	(games: Tournament[]) => _sort(games, stakesSort),
	(games: Tournament[]) => _sort(games, 'name', true),
	(games: Tournament[]) => _sort(games, 'startTime', true),
	(games: Tournament[]) => _sort(games, statusSort),
]

export function hasPlayersSort(t1: SitAndGo, t2: SitAndGo): 1 | 0 | -1 {
	const hasRegistered1 = t1.registered > 0
	const hasRegistered2 = t2.registered > 0

	if (hasRegistered1) return hasRegistered2 ? 0 : -1

	if (hasRegistered2) return hasRegistered1 ? 0 : 1

	return 0
}

export function bigBlindSort(t1: RingGame | Surge, t2: RingGame | Surge): number {
	return Number(t2.bigBlind) - Number(t1.bigBlind)
}

export function stakesSort(t1: SitAndGo | Tournament, t2: SitAndGo | Tournament): number {
	return Number(getStakes(t2)) - Number(getStakes(t1))
}

export function statusSort(t1: Tournament | SitAndGo, t2: Tournament | SitAndGo): number {
	const r1 = getStatusRank(t1.status, isRegistrationOpen(t1))
	const r2 = getStatusRank(t2.status, isRegistrationOpen(t2))

	return r2 - r1
}

function getStatusRank(s: Status, registrationOpen: boolean): 0 | 1 | 2 | 3 | 4 | 5 {
	if (registrationOpen) {
		return s === Status.RUNNING || s === Status.ON_BREAK || s === Status.PREPARING_BREAK
			? 5 // Show late reg first.
			: 4 // Standard registration next.
	}

	switch (s) {
		case Status.RUNNING:
		case Status.ON_BREAK:
		case Status.PREPARING_BREAK:
			return 3
		case Status.ANNOUNCED:
			return 2
		case Status.CANCELLED:
		case Status.FINISHED:
			return 1
		default:
			return 0
	}
}

function isRegistrationOpen(game: Tournament | SitAndGo) {
	// Disabling rule to use abstract equality check for both null and undefined
	// eslint-disable-next-line eqeqeq
	if ((game as Tournament).registrationOpen != null) {
		return (game as Tournament).registrationOpen
	} else {
		return game.status === Status.REGISTERING
	}
}

function getStakes(game: SitAndGo | Tournament): Number {
	const isTournament = !!(game as Tournament).bountyFee || !!(game as Tournament).mysteryBountyFee
	if (isTournament) {
		const tournament = game as Tournament
		return Number(tournament.buyIn) +
			Number(tournament.fee) +
			(Number(tournament.bountyFee) || 0) +
			(Number(tournament.mysteryBountyFee) || 0)
	} else {
		return Number(game.buyIn) + Number(game.fee)
	}
}
